import { useQuery } from "@tanstack/react-query";
import {
  postApiIntegrationsProductItemsQuery,
  type IntegrationProvider,
  type IntegrationSyncStatus,
  type ListProductItemIntegrationSyncsQuery,
} from "apiClients/base-generated";
import { getHeyApiRequestOptions, normalizeHeyApiLang } from "apiClients/base/heyApiRequestOptions";

function heyApiResultToError(err: unknown): Error {
  const message = typeof err === "string" ? err : JSON.stringify(err);
  return new Error(message);
}

/** Default page size; must match initial state on consumers that paginate (e.g. Northmill). */
export const DEFAULT_INTEGRATION_PRODUCTS_PAGE_SIZE = 20;

export type IntegrationProductsQueryPagination = {
  page: number;
  pageSize: number;
};

/** `all` = no lock filter; `locked` / `unlocked` map to API `activeLockOnly`. */
export type IntegrationProductsLockFilter = "all" | "locked" | "unlocked";

/** `all` = no active/inactive filter; otherwise maps to API `isInactive`. */
export type IntegrationProductsActivityFilter = "all" | "active" | "inactive";

/** `all` = omit API field; `variant` / `notVariant` map to `isVariantProduct` true / false. */
export type IntegrationProductsVariantFilter = "all" | "variant" | "notVariant";

export type IntegrationProductsQueryFilters = {
  lock?: IntegrationProductsLockFilter;
  activity?: IntegrationProductsActivityFilter;
  variant?: IntegrationProductsVariantFilter;
  /** Passed to API `ListProductItemIntegrationSyncsQuery.search` (trimmed; empty omits the field). */
  search?: string;
  /** Passed to API `ListProductItemIntegrationSyncsQuery.northmillGroupId` (trimmed; empty omits). */
  northmillGroupId?: string;
};

export function useIntegrationProductsQuery(
  locale: string | null | undefined,
  integrationProvider: IntegrationProvider,
  syncStatus: IntegrationSyncStatus,
  filters?: IntegrationProductsQueryFilters,
  pagination?: IntegrationProductsQueryPagination,
) {
  const lang = normalizeHeyApiLang(locale);
  const lock = filters?.lock ?? "all";
  const activity = filters?.activity ?? "all";
  const variant = filters?.variant ?? "all";
  const searchTrimmed = filters?.search?.trim() ?? "";
  const northmillGroupIdTrimmed = filters?.northmillGroupId?.trim() ?? "";
  const page = pagination?.page ?? 1;
  const pageSize = pagination?.pageSize ?? DEFAULT_INTEGRATION_PRODUCTS_PAGE_SIZE;

  return useQuery({
    queryKey: [
      "integration-products",
      lang ?? "default",
      integrationProvider,
      syncStatus,
      page,
      pageSize,
      lock,
      activity,
      variant,
      searchTrimmed,
      northmillGroupIdTrimmed,
    ],
    queryFn: async () => {
      const body: ListProductItemIntegrationSyncsQuery = {
        page,
        pageSize,
        syncStatus,
        integrationProvider,
      };
      if (lock === "locked") {
        body.activeLockOnly = true;
      } else if (lock === "unlocked") {
        body.activeLockOnly = false;
      }
      if (activity === "inactive") {
        body.isInactive = true;
      } else if (activity === "active") {
        body.isInactive = false;
      }
      if (searchTrimmed) {
        body.search = searchTrimmed;
      }
      if (variant === "variant") {
        body.isVariantProduct = true;
      } else if (variant === "notVariant") {
        body.isVariantProduct = false;
      }
      if (northmillGroupIdTrimmed) {
        body.northmillGroupId = northmillGroupIdTrimmed;
      }

      const result = await postApiIntegrationsProductItemsQuery({
        ...getHeyApiRequestOptions(lang),
        body,
      });
      if (result.error) {
        throw heyApiResultToError(result.error);
      }
      return result.data ?? null;
    },
  });
}
